import { useEffect } from 'react';
import { EventSource, type ErrorEvent } from 'eventsource';
import { useAuthStore } from '@/stores/auth.store';
import { refreshIfNeeded, runRefresh } from '@/auth/token-interceptor';
import { env } from '@/env';

const baseUrl = env.apiUrl + '/api';

const MAX_BACKOFF_MS = 60_000;
const BASE_BACKOFF_MS = 5_000;

/**
 * The one place the dashboard opens a Server-Sent Events stream.
 *
 * Uses the `eventsource` package rather than the browser's built-in
 * `EventSource`, because the built-in one cannot send an `Authorization`
 * header. The token therefore used to ride in the query string - a snapshot
 * taken when the stream opened, which went stale the moment the token expired
 * and left the stream dead with a 401 (see
 * docs/bugs/2026-08-06-sse-stream-401-expired-token.md). Here the `fetch`
 * override reads the token from the store on EVERY request, so each reconnect
 * carries a current one, and a 401 is recoverable instead of terminal:
 *
 *   401/403 → refresh once (shared, deduplicated) → re-open; after that, retry
 *   with exponential backoff while the session is still authenticated, and
 *   re-open immediately when the app resumes or the network returns.
 *   anything else → the library reconnects on its own (3s, or the server's
 *   `retry:`); the per-view polling backstop covers the gap.
 *
 * `onEvent` fires for the named server event (`invalidate`); `ping` frames are
 * the heartbeat and are only logged.
 */
export function useRealtimeStream({
  path,
  label,
  enabled = true,
  onEvent,
}: {
  /** Stream path under /api, e.g. `restaurants/abc/orders/stream`. */
  path: string | null;
  /** Human name used in log lines, e.g. "orders". */
  label: string;
  enabled?: boolean;
  onEvent: () => void;
}): void {
  useEffect(() => {
    if (!enabled || !path) return;

    let source: EventSource | null = null;
    let closed = false;
    let connecting = false;
    let immediateRetryUsed = false;
    let backoffCount = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const clearRetryTimer = () => {
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
    };

    const open = () => {
      connecting = false;
      if (closed) return;
      source?.close();
      source = new EventSource(`${baseUrl}/${path}`, {
        // Read the token at REQUEST time, never from a closure captured at
        // mount - that is what made the old query-param token go stale.
        fetch: (input, init) =>
          fetch(input, {
            ...init,
            headers: {
              ...init.headers,
              Authorization: `Bearer ${useAuthStore.getState().accessToken ?? ''}`,
            },
          }),
      });
      console.info(`[sse] connecting - ${label} stream (${path})`);

      source.addEventListener('open', () => {
        immediateRetryUsed = false;
        backoffCount = 0;
        console.info(`[sse] ready - live ${label} updates connected`);
        // Recover whatever happened while the socket was down. SSE has no
        // replay and this channel carries a signal only, so a reconnect that
        // just logs "ready" silently drops every event from the gap - which is
        // exactly how an order placed while the phone slept stayed invisible
        // (see e2e/journeys/dashboard/orders-blackout.spec.ts). Refetching on
        // every open costs one request per connect and closes the gap.
        onEvent();
      });

      source.addEventListener('invalidate', () => {
        console.info(`[sse] invalidate received → refetching ${label}`);
        onEvent();
      });

      source.addEventListener('ping', () => {
        console.debug(`[sse] heartbeat (${label})`);
      });

      source.addEventListener('error', (event: ErrorEvent) => {
        const code = event.code;

        if (code === 401 || code === 403) {
          source?.close();
          source = null;

          if (!immediateRetryUsed) {
            immediateRetryUsed = true;
            console.warn(
              `[sse] ${label} stream rejected (${code}) - refreshing token and re-opening`,
            );
            connecting = true;
            void runRefresh().then((ok) => {
              if (!ok) {
                connecting = false;
                scheduleRetry();
                return;
              }
              open();
            });
            return;
          }

          scheduleRetry();
          return;
        }

        // Everything else is a transport-level drop; the library reconnects.
        console.debug(
          `[sse] ${label} connection dropped (${event.message ?? 'no message'}) - reconnecting`,
        );
      });
    };

    const scheduleRetry = () => {
      if (closed) return;

      if (!useAuthStore.getState().isAuthenticated) {
        console.info(`[sse] session expired - ${label} stream will not retry`);
        return;
      }

      const delay = Math.min(BASE_BACKOFF_MS * 2 ** backoffCount, MAX_BACKOFF_MS);
      backoffCount++;
      console.warn(`[sse] ${label} stream retry in ${delay / 1000}s (attempt ${backoffCount})`);

      clearRetryTimer();
      retryTimer = setTimeout(() => {
        retryTimer = null;
        if (closed) return;
        connecting = true;
        void refreshIfNeeded().then(open);
      }, delay);
    };

    const tryRecover = () => {
      if (closed || connecting || source !== null) return;
      if (!useAuthStore.getState().isAuthenticated) return;

      clearRetryTimer();
      immediateRetryUsed = false;
      backoffCount = 0;
      connecting = true;
      void refreshIfNeeded().then(open);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') tryRecover();
    };

    const onOnline = () => tryRecover();

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', onOnline);

    // Never open with a token that is already inside the expiry buffer.
    connecting = true;
    void refreshIfNeeded().then(open);

    return () => {
      closed = true;
      clearRetryTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', onOnline);
      console.info(`[sse] closing ${label} stream`);
      source?.close();
    };
  }, [path, label, enabled, onEvent]);
}
